import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { BehaviorSubject, map, Observable, switchMap, take, tap } from 'rxjs';

import { LOGOUT_ON_401 } from '../../../core/interceptors/auth-context';
import {
  ChangePasswordPayload,
  ResetPasswordPayload,
  User,
  UserLogged,
} from '../../models';
import { AppMessageService } from '../shared/app-message.service';
import { AuthEventsService } from '../shared/auth-events.service';
import { PermissionService } from './permission.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly appMessageService = inject(AppMessageService);
  private readonly authEvents = inject(AuthEventsService);
  private readonly destroyRef = inject(DestroyRef);
  private isLoggingOut = false;

  private readonly AUTH_URL = 'api/auth';
  private readonly USERS_URL = 'api/users';

  private readonly userKey: string = 'user';
  private currentUser$ = new BehaviorSubject<User | null>(null);
  private currentUserToken$ = new BehaviorSubject<string | null>(null);

  constructor() {
    const userString = sessionStorage.getItem(this.userKey);

    if (userString !== null) {
      const parsedUser: UserLogged = JSON.parse(userString);
      this.currentUser$.next(parsedUser.user);
      this.currentUserToken$.next(parsedUser.jwt);
      this.permissionService.loadKeys().pipe(take(1)).subscribe();
    } else {
      this.currentUser$.next(null);
      this.currentUserToken$.next(null);
    }

    this.authEvents.onLogout$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.logout());
  }

  /**
   * Login to Power Suite back
   * @param user
   */
  public login(user: User): Observable<User> {
    return this.http
      .post<UserLogged>(
        `${this.AUTH_URL}/local`,
        {
          identifier: user.email,
          password: user.password,
        },
        { context: new HttpContext().set(LOGOUT_ON_401, false) },
      )
      .pipe(
        tap((userLogged: UserLogged) => {
          sessionStorage.setItem(this.userKey, JSON.stringify(userLogged));
          this.currentUser$.next(userLogged.user);
          this.currentUserToken$.next(userLogged.jwt);
        }),
        switchMap((userLogged: UserLogged) => {
          // After login, clean and reload permission keys, then emit the user
          this.permissionService.cleanKeys();
          return this.permissionService
            .loadKeys()
            .pipe(map(() => userLogged.user));
        }),
      );
  }

  /**
   * Logout from app
   * The user stored is removed
   */
  public logout(): void {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    sessionStorage.clear();
    try {
      // Clear persisted projects columns preferences on logout
      localStorage.removeItem('projects:columns');
    } catch (err) {
      // Log storage removal failure for troubleshooting
      console.error('Failed to remove projects:columns on logout', err);
    }
    this.currentUser$.next(null);
    this.currentUserToken$.next(null);
    this.appMessageService.clear();
    // Après un logout, on supprime les anciennes clefs
    this.permissionService.cleanKeys();
    this.router.navigate(['login']).finally(() => {
      this.isLoggingOut = false;
    });
  }

  public getAccessToken() {
    return this.currentToken;
  }

  /**
   * Get current user
   */
  public get currentUser() {
    return this.currentUser$.value;
  }

  public userChanges(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  public getCurrentUser(): Observable<User> {
    return this.http
      .get<User>(`${this.USERS_URL}/me`, {
        params: { populate: ['role', 'clients'] },
      })
      .pipe(
        tap((user: User) => {
          this.currentUser$.next(user);
        }),
      );
  }

  public patchUser(user: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.USERS_URL}/${user.id}`, user).pipe(
      switchMap(() => this.getCurrentUser()),
      tap((fullUser: User) => {
        const storedUserString = sessionStorage.getItem(this.userKey);
        const storedAuth: UserLogged | null = storedUserString
          ? (JSON.parse(storedUserString) as UserLogged)
          : null;
        const jwt = storedAuth?.jwt ?? this.currentUserToken$.value ?? '';

        const updatedAuth: UserLogged = {
          user: fullUser,
          jwt,
        };

        sessionStorage.setItem(this.userKey, JSON.stringify(updatedAuth));
      }),
    );
  }

  public changePassword(payload: ChangePasswordPayload): Observable<void> {
    return this.http.post<void>(`${this.AUTH_URL}/change-password`, payload, {
      context: new HttpContext().set(LOGOUT_ON_401, false),
    });
  }

  public requestPasswordReset(email: string): Observable<void> {
    return this.http.post<void>(
      `${this.AUTH_URL}/forgot-password`,
      { email },
      { context: new HttpContext().set(LOGOUT_ON_401, false) },
    );
  }

  public resetPassword(payload: ResetPasswordPayload): Observable<void> {
    return this.http.post<void>(`${this.AUTH_URL}/reset-password`, payload);
  }

  /**
   * Get current token stocked in local storage
   * @private
   */
  private get currentToken() {
    return this.currentUserToken$.value;
  }
}
